import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'
import type { DisciplineInfo } from '@brighterly/lib-core-types/discipline/interfaces'
import type { FeedbackOptions } from '../model/interfaces'

import { useToast } from '@shared/lib/toast'
import { useCommonStore } from '@shared/model'
import { useCsRoutes } from '@shared/api'

import { useFeedbackStore } from '../model/store'
import { FeedbackSurveyType } from '../model/store/enums'

interface FeedbackOptionsApiOptions {
  onSuccess?: () => void
  onError?: () => void
}

const resolveSurveyType = (options: FeedbackOptions): FeedbackSurveyType => {
  if (options.is_first_lesson) return FeedbackSurveyType.AfterFirstLessonFeedback
  if (options.is_first_lesson_with_new_tutor) return FeedbackSurveyType.AfterFirstLessonWithNewTutorFeedback
  return FeedbackSurveyType.AfterLessonFeedback
}

export const useFeedbackOptionsApi = async (bookingId: number, handlers?: FeedbackOptionsApiOptions): Promise<void> => {
  const { $axios } = useNuxtApp()

  const commonStore = useCommonStore()
  const feedbackStore = useFeedbackStore()
  const toast = useToast()
  const route = useRoute()
  const router = useRouter()

  const url = useCsRoutes('feedback.options', { '[booking_id]': bookingId })
  const setError = (): void => handlers?.onError?.()

  if (!url) {
    setError()
    return
  }

  try {
    commonStore.setLoading(true)
    const { data } = await $axios.get(url)

    const booking: Booking | null = data?.booking || null

    if (!booking) {
      toast.error({
        title: 'Unexpected error',
        description: 'Booking not found. Please refresh the page and try again.',
      })
      setError()
      return
    }

    const discipline: DisciplineInfo | null = data?.discipline || null

    if (!discipline) {
      toast.error({
        title: 'Unexpected error',
        description: 'Discipline not found. Please refresh the page and try again.',
      })
      setError()
      return
    }

    const options: FeedbackOptions | null = data?.options || null

    if (!options) {
      toast.error({
        title: 'Unexpected error',
        description: 'Feedback not found. Please refresh the page and try again.',
      })
    }

    feedbackStore.setDiscipline(discipline)
    feedbackStore.setBooking(booking)
    feedbackStore.setOptions(options)

    if (options) {
      feedbackStore.setSurveyType(resolveSurveyType(options))
    }

    const stepOrder = feedbackStore.getStepOrder
    if (!stepOrder.includes(feedbackStore.getStep)) {
      const targetStep = stepOrder[0]!
      feedbackStore.setStep(targetStep)
      await router.replace({ query: { ...route.query, step: targetStep } })
    }

    feedbackStore.setReady(true)
    handlers?.onSuccess?.()
  } catch {
    setError()
    return
  } finally {
    commonStore.setLoading(false)
  }
}
